import React, { createContext, useContext, useState, useEffect } from 'react';
import { 
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  GoogleAuthProvider,
  signInWithPopup,
  updateProfile
} from 'firebase/auth';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { auth, db } from '../firebase/config';

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);

  async function signup(email, password, displayName) {
    try {
      // First create the authentication user
      const result = await createUserWithEmailAndPassword(auth, email, password);
      
      // Update the user profile with display name
      await updateProfile(result.user, { displayName });
      
      try {
        // Then try to create the user document
        await setDoc(doc(db, 'users', result.user.uid), {
          email,
          displayName,
          createdAt: new Date().toISOString(),
          settings: {
            defaultEffectType: 'slow',
            defaultSpeedFactor: 0.85,
            defaultDeepness: 1.0
          }
        });
      } catch (firestoreError) {
        console.error('Error creating user document:', firestoreError);
        // Even if Firestore fails, we still return the auth result
        // The document can be created later when permissions are correct
      }
      
      return result;
    } catch (error) {
      throw error;
    }
  }

  async function login(email, password) {
    return signInWithEmailAndPassword(auth, email, password);
  }

  async function loginWithGoogle() {
    const provider = new GoogleAuthProvider();
    try {
      const result = await signInWithPopup(auth, provider);
      const userDoc = await getDoc(doc(db, 'users', result.user.uid));
      
      if (!userDoc.exists()) {
        await setDoc(doc(db, 'users', result.user.uid), {
          email: result.user.email,
          displayName: result.user.displayName,
          createdAt: new Date().toISOString(),
          settings: {
            defaultEffectType: 'slow',
            defaultSpeedFactor: 0.85,
            defaultDeepness: 1.0
          }
        });
      }
      return result;
    } catch (error) {
      throw error;
    }
  }

  function logout() {
    return signOut(auth);
  }

  async function getUserSettings() {
    if (!currentUser) return null;
    const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
    return userDoc.data()?.settings;
  }

  async function updateUserSettings(newSettings) {
    if (!currentUser) return;
    const userRef = doc(db, 'users', currentUser.uid);
    const userDoc = await getDoc(userRef);
    
    // If user document doesn't exist, create it with default values
    if (!userDoc.exists()) {
      await setDoc(userRef, {
        email: currentUser.email,
        displayName: currentUser.displayName,
        createdAt: new Date().toISOString(),
        settings: {
          defaultEffectType: 'slow',
          defaultSpeedFactor: 0.85,
          defaultDeepness: 1.0,
          ...newSettings
        }
      });
      return;
    }

    // If document exists, update it
    const userData = userDoc.data();
    await setDoc(userRef, {
      ...userData,
      settings: {
        ...(userData.settings || {}),  // Use empty object as fallback if settings is undefined
        ...newSettings
      }
    });
  }

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const value = {
    currentUser,
    signup,
    login,
    loginWithGoogle,
    logout,
    getUserSettings,
    updateUserSettings
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}
